/**
 * @file product-form-utils.ts
 *
 * Pure helpers for the product form: deriving editable fields from a product,
 * dirty comparison, price validation (locale-tolerant, accepting `,` or `.`),
 * and building the {@link ShopProductPayload} sent on save. Keeping these pure
 * makes the validation and mapping logic unit-testable without rendering.
 */

import type {
  ShopOptionValue,
  ShopProduct,
  ShopProductAttribute,
  ShopProductPayload,
  ShopProductVariant,
  WcProductImageWrite,
} from '../../api/shop-types';
import type { DownloadableFile, ProductFormFields } from './product-form-types';

/**
 * Folds an option-value label the way the commerce-engine compares them — trimmed and
 * case-insensitively (`product_option_values_option_value_uq` is a `lower(value)` unique).
 * Every label comparison in the option editor goes through this one fold so the client and
 * the engine can never disagree on what "the same value" means.
 *
 * @param value - The label as typed or stored.
 *
 * @returns The folded comparison key.
 */
export function foldOptionValue(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * What a typed label means for an option's value list: is it a new value, the same value
 * re-typed, or someone else's?
 *
 * The verdict is shared; the policy is not. Typing into the values box and renaming a pill
 * in place ask this same question and then act on it differently — entry re-labels
 * whichever value the text folds onto, while a rename must REFUSE to fold one value onto
 * another (the engine rejects two values with one folded label in an option,
 * `product_option_duplicate` 422). Sharing the classifier keeps the two policies agreeing
 * on what they are looking at.
 */
export type LabelResolution =
  | { kind: 'new' }
  | { kind: 'same-value'; index: number }
  | { kind: 'exact-duplicate'; index: number }
  | { kind: 'collides'; index: number };

/**
 * Classifies `candidate` against `values`, from the point of view of the value at
 * `targetIndex` when one is being renamed.
 *
 * Passing `targetIndex` is what separates "I am re-typing my own value" (a case-only
 * rename, always allowed) from "I am typing over someone else's" — a distinction the
 * caller cannot recover afterwards from a bare index.
 *
 * @param values - The option's values as they stand.
 * @param candidate - The text being typed or committed (already trimmed).
 * @param targetIndex - The value being renamed, when this is a rename.
 *
 * @returns What the candidate means for this list.
 */
export function resolveLabel(
  values: ShopOptionValue[],
  candidate: string,
  targetIndex?: number,
): LabelResolution {
  const folded = foldOptionValue(candidate);
  const index = values.findIndex((v) => foldOptionValue(v.value) === folded);
  if (index === -1) return { kind: 'new' };
  if (index === targetIndex) return { kind: 'same-value', index };
  if (values[index].value === candidate) return { kind: 'exact-duplicate', index };
  return { kind: 'collides', index };
}

/**
 * Lists the option values an edit destroys — the ones whose variants (price, stock, SKU)
 * the engine will soft-delete on save. Answers the question the way the engine's
 * reconcile answers it, in the same two passes:
 *
 * 1. **Id claims come first.** A value whose id survives the edit survives RELABELLED —
 *    a rename is an update in place, never a loss.
 * 2. **Then folded labels, over the id-LESS entries only.** A value removed and retyped
 *    id-less with the same folded label is reclaimed by the engine, so it is not a loss
 *    either. Folding against id-BEARING entries too would let a sibling's rename swallow
 *    the warning for a genuine deletion (`M` renamed to `L` while the real `L` is
 *    removed).
 *
 * @param before - The option's values as last saved.
 * @param after - The option's values as edited.
 *
 * @returns The labels of the values the save would destroy, as last saved.
 */
export function removedOptionValues(
  before: ShopOptionValue[],
  after: ShopOptionValue[],
): string[] {
  const keptIds = new Set(after.map((v) => v.id).filter(Boolean));
  const unclaimedFolds = new Set(
    after.filter((v) => !v.id).map((v) => foldOptionValue(v.value)),
  );
  const allFolds = new Set(after.map((v) => foldOptionValue(v.value)));
  return before
    .filter((v) =>
      v.id
        ? !keptIds.has(v.id) && !unclaimedFolds.has(foldOptionValue(v.value))
        : !allFolds.has(foldOptionValue(v.value)),
    )
    .map((v) => v.value);
}

/**
 * The labels an axis edit destroys, across ALL axes — {@link removedOptionValues} per
 * surviving axis, plus every value of a deleted axis. Feeds the save-time loss modal's
 * message. A prev axis is matched to its edited self by slug first (unchanged name), then
 * by any shared value id (the same axis renamed — its slug moves with the name).
 *
 * @param prev - The axes as last applied.
 * @param next - The axes as edited.
 *
 * @returns The labels of the values the edit would destroy.
 */
export function removedAxisValueLabels(
  prev: ShopProductAttribute[],
  next: ShopProductAttribute[],
): string[] {
  const lost: string[] = [];
  for (const prevAxis of prev) {
    const prevIds = new Set(prevAxis.options.map((o) => o.id).filter(Boolean));
    const nextAxis =
      next.find((a) => a.slug === prevAxis.slug) ??
      next.find((a) => a.options.some((o) => o.id && prevIds.has(o.id)));
    if (!nextAxis) {
      lost.push(...prevAxis.options.map((o) => o.value));
    } else {
      lost.push(...removedOptionValues(prevAxis.options, nextAxis.options));
    }
  }
  return lost;
}

/**
 * Rewrites the variant rows' option selections for values (and axes) that an edit
 * RENAMED in place — matched by value id, the identity the engine keeps. A row selecting
 * `Size: Blu` follows a `Blu` → `Blue` rename (and a `Size` → `Fit` axis rename) instead
 * of being pruned as though its combination were removed; only a value whose id is gone
 * leaves its rows to the prune. Returns the SAME array reference when nothing changed.
 *
 * @param prev - The axes as last applied (what the rows' selections currently read).
 * @param next - The axes as edited.
 * @param rows - The materialised variant rows.
 *
 * @returns The rows with renamed selections rewritten, or `rows` itself when none were.
 */
export function relabelRowSelections(
  prev: ShopProductAttribute[],
  next: ShopProductAttribute[],
  rows: ShopProductVariant[],
): ShopProductVariant[] {
  const renameList = buildSelectionRenames(prev, next);
  if (renameList.length === 0) return rows;

  let changed = false;
  const rewritten = rows.map((row) => {
    const options = relabelSelections(renameList, row.options);
    if (options === row.options) return row;
    changed = true;
    // The display label is the joined selection values (products.ts `variantLabel`), so it
    // follows the rename with them.
    return { ...row, options, label: options.map((sel) => sel.value).join(' / ') };
  });
  return changed ? rewritten : rows;
}

/**
 * Rewrites bare selector lists (no rows around them) for an edit's in-place renames —
 * the same id-matched rewrite {@link relabelRowSelections} applies to the table rows,
 * for the form's remembered combination sets (e.g. the suppressed-at-load combinations),
 * which would otherwise go stale the first time an axis or value they name is renamed.
 *
 * @param prev - The axes as last applied.
 * @param next - The axes as edited.
 * @param lists - The selector lists to rewrite.
 *
 * @returns The rewritten lists (each list is the same reference when untouched).
 */
export function relabelSelectionLists(
  prev: ShopProductAttribute[],
  next: ShopProductAttribute[],
  lists: { name: string; value: string }[][],
): { name: string; value: string }[][] {
  const renameList = buildSelectionRenames(prev, next);
  if (renameList.length === 0) return lists;
  return lists.map((list) => relabelSelections(renameList, list));
}

/** One id-preserved rename: what the selection reads today → what it should read. */
interface SelectionRename {
  from: { name: string; value: string };
  to: { name: string; value: string };
}

/** The id-matched renames between two axis lists (a value or axis relabelled in place). */
function buildSelectionRenames(
  prev: ShopProductAttribute[],
  next: ShopProductAttribute[],
): SelectionRename[] {
  // id → what the selection reads today / should read after the edit.
  const byId = (axes: ShopProductAttribute[]): Map<string, { name: string; value: string }> => {
    const map = new Map<string, { name: string; value: string }>();
    for (const axis of axes) {
      for (const option of axis.options) {
        if (option.id) map.set(option.id, { name: axis.name, value: option.value });
      }
    }
    return map;
  };
  const prevById = byId(prev);
  const nextById = byId(next);

  const renames: SelectionRename[] = [];
  for (const [id, from] of prevById) {
    const to = nextById.get(id);
    if (to && (to.name !== from.name || to.value !== from.value)) {
      renames.push({ from, to });
    }
  }
  return renames;
}

/** Applies the renames to one selector list; returns the SAME reference when untouched. */
function relabelSelections(
  renameList: SelectionRename[],
  selections: { name: string; value: string }[],
): { name: string; value: string }[] {
  let changed = false;
  const rewritten = selections.map((sel) => {
    const rename = renameList.find(
      (r) =>
        foldOptionValue(r.from.name) === foldOptionValue(sel.name) &&
        foldOptionValue(r.from.value) === foldOptionValue(sel.value),
    );
    if (!rename) return sel;
    changed = true;
    return { ...sel, name: rename.to.name, value: rename.to.value };
  });
  return changed ? rewritten : selections;
}

/**
 * The live cartesian count of a variation matrix (product of per-axis value counts). `0` for
 * a product with no axes. Drives the "N of {max} combinations" counter and the client cap.
 *
 * @param attributes - The variation axes.
 *
 * @returns The number of generated combinations.
 */
export function countCombinations(attributes: ShopProductAttribute[]): number {
  return attributes.length === 0
    ? 0
    : attributes.reduce((total, axis) => total * axis.options.length, 1);
}

/**
 * Counts the materialized (already-saved) variants whose combination references a value
 * LABEL the pending axis edit drops. The caller runs {@link relabelRowSelections} FIRST, so
 * an id-preserving rename (the pill editor's in-place gesture) arrives here already rewritten
 * onto the new labels and is never counted as a loss. The folded-label comparison then covers
 * the id-less paths: a value removed and re-typed without its id is preserved by the engine's
 * folded-label fallback (including a pure case fix, which it recases in place), so only a
 * genuinely dropped label retires its variants and discards their per-variant price/SKU/stock
 * — this drives the "you'll lose data" confirm. Folding matches the engine (`fold` in
 * `reconcileOptionStructure` / `reconcileOptionValues`); comparing raw labels would warn of a
 * loss on "red"→"Red", where nothing is lost.
 *
 * @param next - The pending axes.
 * @param variants - The materialized variant rows.
 *
 * @returns The number of sellable variants the edit would retire.
 */
export function variantsLostByAxisEdit(
  next: ShopProductAttribute[],
  variants: ShopProductVariant[],
): number {
  const fold = (label: string): string => label.trim().toLowerCase();
  const valuesByName = new Map(
    next.map((axis) => [fold(axis.name), new Set(axis.options.map((o) => fold(o.value)))]),
  );
  return variants.filter(
    (variant) =>
      variant.enabled &&
      variant.options.some((sel) => !valuesByName.get(fold(sel.name))?.has(fold(sel.value))),
  ).length;
}

/**
 * Finds the variants whose SKU collides with another row's, compared the way the engine
 * compares them — trimmed and case-insensitively (the SKU registry is a `lower()` unique).
 * Catching this in the table matters because the batch is all-or-nothing: the engine
 * rejects the whole write over one duplicate, so without a local check the merchant loses
 * every other edit in the same save to a mistake in one cell. Blank SKUs never collide —
 * the engine generates those.
 *
 * @param variants - The rows to compare (the caller passes only sellable rows).
 *
 * @returns The ids of every row sharing a SKU with at least one other row.
 */
export function duplicateSkuVariantIds(
  variants: readonly { id: string; sku: string }[],
): Set<string> {
  const idsBySku = new Map<string, string[]>();
  for (const { id, sku } of variants) {
    const folded = sku.trim().toLowerCase();
    if (folded === '') continue;
    idsBySku.set(folded, [...(idsBySku.get(folded) ?? []), id]);
  }
  const duplicates = new Set<string>();
  idsBySku.forEach((ids) => {
    if (ids.length > 1) ids.forEach((id) => duplicates.add(id));
  });
  return duplicates;
}

/**
 * Per-field validation reasons for a single variant row, mapped to a user-facing
 * message by the table. A field key is absent when that field is valid. `price`
 * `'invalid'` covers both an unparseable and a negative amount; `salePrice`
 * `'tooHigh'` means it is not strictly below the row's price; `stock` `'invalid'`
 * means the tracked quantity is not a whole number `>= 0`; `sku` `'duplicate'`
 * means another enabled row uses the same folded SKU.
 */
export interface VariantRowErrors {
  price?: 'required' | 'invalid';
  salePrice?: 'invalid' | 'tooHigh';
  stock?: 'invalid';
  sku?: 'duplicate';
}

/**
 * Validates the editable money/stock fields of a single variant row: the price must be
 * present, parseable, and `>= 0`; a set sale price must be parseable, `>= 0`, and strictly
 * below the price; a tracked stock quantity must be a whole number `>= 0`. The caller only
 * validates enabled rows (a retired row is not sold, so its fields are not checked).
 *
 * @param variant - The variant row to validate.
 *
 * @returns The per-field errors, or `null` when every field is valid.
 */
export function validateVariantRow(variant: ShopProductVariant): VariantRowErrors | null {
  const errors: VariantRowErrors = {};

  const price = parsePrice(variant.regular_price);
  if (variant.regular_price.trim() === '') {
    errors.price = 'required';
  } else if (price === null || price < 0) {
    errors.price = 'invalid';
  }

  if (variant.sale_price.trim() !== '') {
    const sale = parsePrice(variant.sale_price);
    if (sale === null || sale < 0) {
      errors.salePrice = 'invalid';
    } else if (price !== null && price >= 0 && sale >= price) {
      errors.salePrice = 'tooHigh';
    }
  }

  if (variant.manage_stock) {
    const quantity = variant.stock_quantity;
    if (quantity === null || !Number.isInteger(quantity) || quantity < 0) {
      errors.stock = 'invalid';
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Validates every ENABLED variant row and collects the per-row errors, keyed by variant id.
 * Retired rows are skipped (a retired row is not sold, so its fields are not checked and its
 * SKU cannot collide). Duplicate SKUs are judged across the enabled rows as a set. An empty
 * result means every enabled row is valid, which the form treats as "safe to submit".
 *
 * @param variants - The current per-variant table rows.
 *
 * @returns A map of variant id to its {@link VariantRowErrors} (empty when all rows are valid).
 */
export function validateVariantRows(
  variants: ShopProductVariant[],
): Record<string, VariantRowErrors> {
  const result: Record<string, VariantRowErrors> = {};
  const enabled = variants.filter((v) => v.enabled);
  const duplicateSkus = duplicateSkuVariantIds(enabled);
  for (const variant of enabled) {
    const rowErrors = validateVariantRow(variant) ?? {};
    if (duplicateSkus.has(variant.id)) {
      rowErrors.sku = 'duplicate';
    }
    if (Object.keys(rowErrors).length > 0) {
      result[variant.id] = rowErrors;
    }
  }
  return result;
}

/**
 * Clears the image of any variant row whose image is no longer one of the product's gallery
 * images — the client-side mirror of the server's unlink cascade (and its
 * `product_variant_image_invalid` write guard): removing a gallery image must visibly drop it
 * from the variants that used it, before the save round-trips. Returns the SAME array
 * reference when nothing changed, so setters and effects keyed on identity stay quiet.
 *
 * @param variants - The current variant rows.
 * @param validImageIds - The ids of the product's current gallery images.
 *
 * @returns The rows with out-of-gallery images cleared, or `variants` itself when none were.
 */
export function pruneVariantImages(
  variants: ShopProductVariant[],
  validImageIds: ReadonlySet<string>,
): ShopProductVariant[] {
  const stale = (v: ShopProductVariant): boolean => v.image !== null && !validImageIds.has(v.image.id);
  if (!variants.some(stale)) {
    return variants;
  }
  return variants.map((v) => (stale(v) ? { ...v, image: null } : v));
}

/**
 * Builds a URL-safe slug from a name (lower-cased, spaces to hyphens). Used as a
 * variant option's slug/id, which is unique because option names are unique.
 *
 * @param name - The display name.
 *
 * @returns The slug.
 */
export function slugify(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '-');
}

/**
 * Parses a user-entered price, tolerating a comma or dot decimal separator.
 *
 * @param value - The raw input.
 *
 * @returns The numeric value, or `null` when empty or unparseable.
 */
function parsePrice(value: string): number | null {
  const trimmed = value.trim().replace(/\s/g, '');
  if (trimmed === '') {
    return null;
  }
  const normalized =
    trimmed.includes(',') && !trimmed.includes('.')
      ? trimmed.replace(',', '.')
      : trimmed.replace(/,/g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Reports whether a price input is present but not a valid, non-negative number.
 * An empty input is treated as valid (a required-price rule is enforced elsewhere);
 * a negative amount is rejected here so `"-5"` is caught at the product level too.
 *
 * @param value - The raw price input.
 *
 * @returns `true` when non-empty and unparseable or negative; `false` when empty or valid.
 */
export function isInvalidPriceInput(value: string): boolean {
  if (value.trim() === '') {
    return false;
  }
  const parsed = parsePrice(value);
  return parsed === null || parsed < 0;
}

/**
 * Reports whether the sale price is not strictly below the regular price.
 *
 * @param regularPrice - The regular price input.
 * @param salePrice - The sale price input.
 * @param fallbackRegularPrice - Used when `regularPrice` is empty (the source
 *   product's effective price), matching the save-time `regularPrice || price`.
 *
 * @returns `true` when both parse and sale ≥ regular; otherwise `false`.
 */
export function isSalePriceVersusRegularInvalid(
  regularPrice: string,
  salePrice: string,
  fallbackRegularPrice = '',
): boolean {
  const sale = parsePrice(salePrice);
  if (sale === null) {
    return false;
  }
  const regular = parsePrice(regularPrice || fallbackRegularPrice);
  if (regular === null) {
    return false;
  }
  return sale >= regular;
}

/**
 * Derives the editable form fields from a source product.
 *
 * @param product - The source product, or `undefined` for create mode.
 * @param duplicate - When `true`, appends “ (Copy)” to the name and clears SKU.
 *
 * @returns The initial {@link ProductFormFields}.
 */
export function deriveFields(
  product?: ShopProduct,
  duplicate = false,
): ProductFormFields {
  const name = product?.name ?? '';
  return {
    productType: product?.virtual ? 'digital' : 'physical',
    name: duplicate && name ? `${name} (Copy)` : name,
    status: product?.status ?? 'publish',
    description: product?.description ?? '',
    shortDescription: product?.short_description ?? '',
    price: product?.price ?? '',
    regularPrice: product?.regular_price ?? '',
    salePrice: product?.sale_price ?? '',
    sku: duplicate ? '' : (product?.sku ?? ''),
    weight: product?.weight ?? '',
    soldIndividually: product?.sold_individually ?? false,
    unlimitedStock: product ? product.stock_quantity === null : false,
    stockQuantity: product?.stock_quantity ?? 0,
  };
}

/**
 * Shallow-compares two field sets for dirty tracking.
 *
 * @param a - One field set.
 * @param b - The other field set.
 *
 * @returns `true` when every field is strictly equal.
 */
export function shallowEqual(a: ProductFormFields, b: ProductFormFields): boolean {
  const keys = Object.keys(a) as (keyof ProductFormFields)[];
  return keys.every((key) => a[key] === b[key]);
}

/**
 * Converts an ordered list of gallery URLs to the API image-write shape,
 * preferring an attachment id when one is known for a URL.
 *
 * @param orderedUrls - Gallery image URLs in display order.
 * @param attachmentIdByUrl - Map of URL to its uploaded attachment id.
 *
 * @returns The image-write entries in the same order.
 */
export function buildWcProductImagesFromGallery(
  orderedUrls: string[],
  attachmentIdByUrl: Record<string, string>,
): WcProductImageWrite[] {
  return orderedUrls.map((url) => {
    const id = attachmentIdByUrl[url];
    return id ? { id } : { src: url };
  });
}

/** Inputs needed to assemble the product write payload. */
export interface BuildProductPayloadArgs {
  fields: ProductFormFields;
  imageUrls: string[];
  imageIdByUrl: Record<string, string>;
  downloads: DownloadableFile[];
  categoryIds: string[];
  attributes: ShopProductAttribute[];
  crossSellIds: string[];
}

/**
 * Builds the write payload from the form's fields and selections.
 *
 * @param args - Fields, media, categories, attributes, and related products.
 *
 * @returns The {@link ShopProductPayload} to POST/PUT.
 */
export function buildProductPayload(
  args: BuildProductPayloadArgs,
): ShopProductPayload {
  const { fields } = args;
  const isDigital = fields.productType === 'digital';
  const manageStock = !fields.unlimitedStock;
  const stockQuantity = fields.unlimitedStock ? null : fields.stockQuantity;
  const inStock = fields.unlimitedStock || fields.stockQuantity > 0;

  return {
    name: fields.name.trim(),
    status: fields.status,
    description: fields.description,
    short_description: fields.shortDescription,
    images: buildWcProductImagesFromGallery(args.imageUrls, args.imageIdByUrl),
    virtual: isDigital,
    downloads: isDigital ? args.downloads : [],
    attributes: args.attributes.map((a) => ({
      name: a.name,
      options: a.options,
      visible: true,
      variation: true,
    })),
    regular_price: fields.regularPrice || fields.price,
    sale_price: fields.salePrice || '',
    sku: fields.sku,
    weight: isDigital ? '' : fields.weight,
    sold_individually: fields.soldIndividually,
    manage_stock: manageStock,
    stock_quantity: stockQuantity,
    stock_status: inStock ? 'instock' : 'outofstock',
    categories: args.categoryIds.map((id) => ({ id })),
    cross_sell_ids: args.crossSellIds,
  };
}
